import type { CarProfile } from './CarProfile';
import { flatSixCupSingle } from './single/flat-six-cup-single';
import { ferrari458Single } from './single/ferrari-458-single';
import { ferrari812Single } from './single/ferrari-812-single';
import { lamborghiniAventadorSingle } from './single/lamborghini-aventador-single';

/**
 * Live line-up: the house GT racer (Marc's cockpit mix), then the prototypes.
 *
 * Everything after flatSixCup is `adminOnly`. That flag is not decoration: vite.config.ts writes
 * dist/cars.json from `available && !adminOnly`, and the server reads THAT file to decide which cars
 * can be bought and whose audio it will sign a grant for. So a prototype is invisible on the home page,
 * refused by /api/checkout and its audio answers 403, for everyone who is not signed in as an admin.
 */
// The GT-R and the F40 are OUT (Marc, 2026-09-01): profiles, audio banks and photographs deleted,
// not just unlisted. Between them they were 37 MB of the 193 MB that `railway up` has to push, and
// that upload has been timing out on the edge of Cloudflare's limit all day.
export const CARS: CarProfile[] = [
  flatSixCupSingle,
  // THE BOUNCES ARE THE CARS (Marc). One file, one ladder, one download - and measured to within a
  // decibel of the four-bank build it came from.
  ferrari458Single,
  ferrari812Single,
  lamborghiniAventadorSingle,
];
// THE 4 VOICES BUILDS ARE OFF THE SHELF (Marc: "ya puedes eliminar los coches q tengan 4 voices").
// Their profiles stay in the tree - `src/cars/single/*` is rendered FROM them and imports them for
// everything a bounce does not change - they are simply not cars any more.


/** Slugs a car used to answer to. A bookmark in somebody's car must never stop working because we
 *  renamed the engine — App.tsx rewrites the address bar to the current slug on arrival. */
const OLD_SLUGS: Record<string, string> = {
  // 2026-09-02: the car's id WAS porsche-911-rsr-copy and the slug was already porsche-911-gt3-cup.
  // The id now matches the slug, so the direction of this map flipped: what used to be the current
  // id is now one of the names it answers to. Old bookmarks and any link already out in the world
  // still resolve; App.tsx rewrites the address bar to the current slug on arrival.
  'porsche-911-rsr-copy': 'porsche-911-gt3-cup',
  'porsche-911-rsr-gte': 'porsche-911-gt3-cup',
  // 2026-09-10: THE LINE-UP WENT GENERIC. The marques and model names came off the shelf (they are
  // somebody else's trademarks and the engines are paid SKUs); the ids stay, because purchases, IAP
  // products, grants and the audio folders are keyed on them. These are the slugs the cars answered
  // to under their old names, so links already out in the world still open the right pit box.
  'lamborghini-aventador-sv': 'lamborghini-aventador',
  'ferrari-812-superfast': 'ferrari-812',
  'ferrari-458-italia': 'ferrari-458',
  'lamborghini-aventador-sv-4v': 'lamborghini-aventador-4v',
  'ferrari-812-superfast-4v': 'ferrari-812-4v',
  'ferrari-458-italia-4v': 'ferrari-458-4v',
  // 2026-09-14: the names moved again (Marc: "V12 770 que puta mierda es esa, pon 770hp"): the number is
  // now read as what it is. The four-day-old slugs stay open too.
  'flat-six-cup': 'porsche-911-gt3-cup',
  'v12-770': 'lamborghini-aventador',
  'v12-800': 'ferrari-812',
  'v8-570': 'ferrari-458',
  'flat-six-cup-2v': 'porsche-911-gt3-cup-2v',
  'v12-770-4v': 'lamborghini-aventador-4v',
  'v12-800-4v': 'ferrari-812-4v',
  'v8-570-4v': 'ferrari-458-4v',
};

/** The names this car was sold under before its current id. A purchase recorded under an old name
 *  is still a purchase of this car; access.ts asks here so a stored entitlement never goes stale. */
export function legacyIdsOf(id: string): string[] {
  return Object.entries(OLD_SLUGS).filter(([, now]) => now === id).map(([old]) => old);
}
/** By id or by URL slug: old links keep working, new ones read like the car. */
export function getCar(id: string): CarProfile | undefined {
  const wanted = OLD_SLUGS[id] ?? id;
  return CARS.find((c) => c.id === wanted || c.slug === wanted);
}
/** What goes in the address bar for this car. */
export function carPath(car: CarProfile): string {
  return car.slug ?? car.id;
}
